import React, { useState } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";
import { formatDateToString, formatUTCDateToString } from "@/lib/utils";
import type { ChildBase, BookablePeriodBase } from "nautika-types";

interface PeriodViewProps {
  child: ChildBase;
  selectedPeriodIds: string[];
  initialMonth?: Date;
}

interface DayPeriods {
  [key: string]: BookablePeriodBase[];
}

export const PeriodView = function PeriodView({ child, selectedPeriodIds, initialMonth }: PeriodViewProps) {
  const [currentMonth, setCurrentMonth] = useState<Date>(() => {
    return initialMonth ? new Date(initialMonth.getFullYear(), initialMonth.getMonth(), 1) : new Date();
  });

  // Générer les mois à charger (mois précédent, actuel, et suivant)
  const monthsToLoad = React.useMemo(() => {
    const months = [];

    // Mois précédent
    const prevMonth = new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1, 1);
    const prevMonthStr = `${prevMonth.getFullYear()}-${(prevMonth.getMonth() + 1).toString().padStart(2, "0")}`;
    months.push(prevMonthStr);

    // Mois actuel
    const currentMonthStr = `${currentMonth.getFullYear()}-${(currentMonth.getMonth() + 1).toString().padStart(2, "0")}`;
    months.push(currentMonthStr);

    // Mois suivant
    const nextMonth = new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 1);
    const nextMonthStr = `${nextMonth.getFullYear()}-${(nextMonth.getMonth() + 1).toString().padStart(2, "0")}`;
    months.push(nextMonthStr);

    return months;
  }, [currentMonth]);

  // Charger les périodes réservables pour les 3 mois
  const prevMonthQuery = api.useQuery("get", "/club/bookable-periods", {
    params: {
      query: { month: monthsToLoad[0] },
    },
    staleTime: 300000, // 5 minutes
  });

  const currentMonthQuery = api.useQuery("get", "/club/bookable-periods", {
    params: {
      query: { month: monthsToLoad[1] },
    },
    staleTime: 300000, // 5 minutes
  });

  const nextMonthQuery = api.useQuery("get", "/club/bookable-periods", {
    params: {
      query: { month: monthsToLoad[2] },
    },
    staleTime: 300000, // 5 minutes
  });

  // Combiner toutes les données des 3 mois
  const allPeriodsData = React.useMemo(() => {
    const allPeriods: BookablePeriodBase[] = [];

    [prevMonthQuery, currentMonthQuery, nextMonthQuery].forEach((query) => {
      if (query.data && Array.isArray(query.data)) {
        allPeriods.push(...(query.data as BookablePeriodBase[]));
      }
    });

    return allPeriods;
  }, [prevMonthQuery, currentMonthQuery, nextMonthQuery]);

  // Organiser les périodes par jour
  const periodsByDay: DayPeriods = React.useMemo(() => {
    const dayPeriods: DayPeriods = {};

    allPeriodsData.forEach((period: BookablePeriodBase) => {
      // Utiliser la date UTC directement pour éviter les problèmes de timezone
      const utcDate = new Date(period.startTime);
      const date = formatUTCDateToString(utcDate);
      if (!dayPeriods[date]) {
        dayPeriods[date] = [];
      }
      dayPeriods[date].push(period);
    });

    return dayPeriods;
  }, [allPeriodsData]);

  // Vérifier si une période est sélectionnée
  const isPeriodSelected = (periodId: string) => {
    return selectedPeriodIds.includes(periodId);
  };

  // Vérifier si le mois actuel contient des périodes bookables
  const hasBookablePeriodsInMonth = React.useMemo(() => {
    const year = currentMonth.getFullYear();
    const month = currentMonth.getMonth() + 1;
    const currentMonthStr = `${year}-${month.toString().padStart(2, "0")}`;

    const currentMonthPeriods = allPeriodsData.filter((period) => {
      const periodDate = new Date(period.startTime);
      const periodYear = periodDate.getFullYear();
      const periodMonth = periodDate.getMonth() + 1;
      const periodMonthStr = `${periodYear}-${periodMonth.toString().padStart(2, "0")}`;
      return periodMonthStr === currentMonthStr;
    });

    return currentMonthPeriods.length > 0;
  }, [allPeriodsData, currentMonth]);

  // Vérifier si les données du mois actuel sont déjà chargées
  const currentMonthDataLoaded = React.useMemo(() => {
    const currentMonthStr = `${currentMonth.getFullYear()}-${(currentMonth.getMonth() + 1).toString().padStart(2, "0")}`;

    const currentMonthIndex = monthsToLoad.findIndex((month) => month === currentMonthStr);

    if (currentMonthIndex === -1) {
      return false;
    }

    const queries = [prevMonthQuery, currentMonthQuery, nextMonthQuery];
    const relevantQuery = queries[currentMonthIndex];

    return relevantQuery && !relevantQuery.isLoading;
  }, [currentMonth, monthsToLoad, prevMonthQuery, currentMonthQuery, nextMonthQuery]);

  // Calendrier déporté en composant de niveau supérieur

  return (
    <div className="flex flex-col gap-y-2">
      <div className="text-lg font-semibold">Périodes réservées</div>
      <div className="text-sm text-gray-700">Affichage des périodes réservées pour {child.firstName}</div>

      {selectedPeriodIds.length === 0 ? (
        <div className="rounded-lg border border-gray-200 bg-gray-50 p-8 text-center">
          <p className="text-gray-500">Aucune période réservée trouvée pour cet enfant</p>
        </div>
      ) : (
        <div className="w-full overflow-x-auto">
          <CustomCalendar
            currentMonth={currentMonth}
            onChangeMonth={setCurrentMonth}
            periodsByDay={periodsByDay}
            isPeriodSelected={isPeriodSelected}
            hasBookablePeriodsInMonth={hasBookablePeriodsInMonth}
            currentMonthDataLoaded={currentMonthDataLoaded}
          />
        </div>
      )}
    </div>
  );
};

const CustomCalendar: React.FC<{
  currentMonth: Date;
  onChangeMonth: (newMonth: Date) => void;
  periodsByDay: DayPeriods;
  isPeriodSelected: (periodId: string) => boolean;
  hasBookablePeriodsInMonth: boolean;
  currentMonthDataLoaded: boolean;
}> = ({ currentMonth, onChangeMonth, periodsByDay, isPeriodSelected, hasBookablePeriodsInMonth, currentMonthDataLoaded }) => {
  const monthStart = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), 1);
  const monthEnd = new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 0);
  const weeks: Date[][] = [];
  const firstDay = new Date(monthStart);
  firstDay.setDate(firstDay.getDate() - firstDay.getDay() + 1);

  const currentDate = new Date(firstDay);

  while (currentDate <= monthEnd || currentDate.getMonth() === monthStart.getMonth()) {
    const week: Date[] = [];
    for (let i = 0; i < 7; i++) {
      week.push(new Date(currentDate));
      currentDate.setDate(currentDate.getDate() + 1);
    }
    weeks.push(week);

    if (currentDate.getMonth() !== monthStart.getMonth() && currentDate.getDate() > 7) {
      break;
    }
  }

  const prevMonth = new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1, 1);
  const nextMonth = new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 1);

  return (
    <div id="period-calendar" className="space-y-6 rounded-lg border p-6">
      {/* Navigation du mois */}
      <div className="relative">
        <div id="month-navigation" className="flex items-center justify-center gap-4">
          <Button variant="outline" size="sm" onClick={() => onChangeMonth(prevMonth)}>
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <h3 className="w-54 text-center text-lg font-semibold capitalize">{currentMonth.toLocaleDateString("fr-FR", { month: "long", year: "numeric" })}</h3>
          <Button variant="outline" size="sm" onClick={() => onChangeMonth(nextMonth)}>
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>

        {/* Légende des couleurs en haut à gauche */}
        <div className="absolute top-0 left-0 flex items-center gap-3 text-xs text-gray-600">
          <div className="flex items-center gap-1">
            <div className="h-3 w-3 rounded border border-blue-300 bg-blue-100"></div>
            <span>Réservé</span>
          </div>
          <div className="flex items-center gap-1">
            <div className="h-3 w-3 rounded border border-gray-200 bg-gray-50 opacity-60"></div>
            <span>Non réservé</span>
          </div>
        </div>
      </div>

      {/* Calendrier */}
      <div className="relative w-full overflow-x-auto">
        {/* Overlay pour les mois sans périodes bookables */}
        {currentMonthDataLoaded && !hasBookablePeriodsInMonth && (
          <div className="absolute inset-0 z-10 flex items-center justify-center bg-white/40 backdrop-blur-xs">
            <div className="rounded-lg border border-gray-200 bg-white p-8 text-center shadow-lg">
              <p className="text-lg font-medium text-gray-600">
                Aucune période réservable en {currentMonth.toLocaleDateString("fr-FR", { month: "long", year: "numeric" })}
              </p>
            </div>
          </div>
        )}

        <div className="grid grid-cols-7 gap-2">
          {/* En-tête */}
          {["Lundi", "Mardi", "Mercredi", "Jeudi", "Vendredi", "Samedi", "Dimanche"].map((day) => (
            <div key={day} className="p-3 text-center text-sm font-medium">
              {day}
            </div>
          ))}

          {/* Semaines */}
          {weeks.map((week, weekIndex) => (
            <React.Fragment key={weekIndex}>
              {week.map((day, dayIndex) => {
                const dateStr = formatDateToString(day);
                const dayPeriods = periodsByDay[dateStr] || [];
                const isCurrentMonth = day.getMonth() === currentMonth.getMonth();
                const hasNoPeriods = dayPeriods.length === 0;

                return (
                  <div
                    key={dayIndex}
                    className={`h-[120px] rounded border p-3 ${!isCurrentMonth ? "bg-gray-50 opacity-30" : hasNoPeriods ? "bg-gray-50" : "bg-white"}`}
                  >
                    <div className={`mb-2 text-center text-sm font-medium ${hasNoPeriods ? "text-gray-400" : ""}`}>{day.getDate()}</div>

                    {/* Conteneur avec hauteur fixe pour l'alignement */}
                    <div className="flex h-[calc(100%-2rem)] flex-col justify-center gap-1">
                      {["matin", "midi", "apres-midi"].map((periodType) => {
                        const period = dayPeriods.find((p) => p.period === periodType);

                        if (!period) {
                          return <div key={periodType} className="invisible h-6"></div>;
                        }

                        const selected = isPeriodSelected(period.id);

                        return (
                          <div
                            key={periodType}
                            className={`flex h-6 w-full items-center justify-center rounded border text-center text-xs ${selected ? "border-blue-300 bg-blue-100 text-blue-700" : "border-gray-200 bg-gray-50 text-gray-400 opacity-60"}`}
                          >
                            {periodType === "matin" ? "Matin" : periodType === "midi" ? "Midi" : "Aprèm"}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </React.Fragment>
          ))}
        </div>
      </div>
    </div>
  );
};
